import React, { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { useTranslation } from '../../translations';
import { isResourceError, useInputState } from '../../utils';
import { captureError } from '../../utils/errorHandling';

interface FindMyReservationProps {
  readonly refresh: () => void;
}

export const FindMyReservation: FC<FindMyReservationProps> = ({ refresh }) => {
  const [showModal, setShowModal] = useState(false);
  const { ts } = useTranslation();

  const toggleModal = () => {
    setShowModal((b) => !b);
  };

  const onRefresh = useCallback(() => {
    setShowModal(false);
    refresh();
  }, [refresh]);

  return (
    <>
      {showModal && <FindMyReservationModal close={toggleModal} refresh={onRefresh} />}
      <Button block color="secondary" id="findMyReservation" onClick={toggleModal} outline>
        {ts('find_existing_reservation')}
      </Button>
    </>
  );
};

interface ModalProps {
  readonly refresh: () => void;
  readonly close: () => void;
}

const FindMyReservationModal: FC<ModalProps> = ({ close, refresh }) => {
  const [reservationId, setReservationId] = useInputState(null, true);
  const { ts } = useTranslation();

  const isSubmitAllowed = reservationId !== null && reservationId.length > 0;

  const onSubmit = useCallback(() => {
    if (!isSubmitAllowed) {
      return;
    }

    api
      .linkHotelReservation(reservationId)
      .then(() => {
        toast.success(ts('your_reservation_has_been_found_and_linked_to_your_account'));
        refresh();
      })
      .catch((error) => {
        if (isResourceError(error, 'Reservation')) {
          toast.error(
            <div>
              <div>
                {ts('we_could_not_find_a_reservation_matching_the_provided_reservation_id')}
              </div>
              <div>{ts('please_ensure_the_reservation_number_is_correct')}</div>
            </div>,
            { autoClose: 10_000 },
          );

          return;
        }

        captureError(error as Error);
      });
  }, [isSubmitAllowed, reservationId, ts, refresh]);

  return (
    <Modal id="findMyReservation-modal" isOpen toggle={close}>
      <ModalHeader toggle={close}>{ts('find_my_reservation')}</ModalHeader>
      <ModalBody>
        <p>
          {ts(
            'hotel.if_you_already_have_a_reservation_you_can_provide_the_reservation_number_and_well_attempt_to_locate_and_link_your_reservation',
          )}
        </p>
        <Alert color="warning" fade={false}>
          {ts(
            'hotel.in_order_to_locate_your_reservation_the_primary_guest_name_and_email_must_match_the_information_listed_under_edit_personal_details',
          )}
        </Alert>
        <hr />
        <FormGroup>
          <Label for="reservationId">{ts('reservation_id')}</Label>
          <Input name="reservationId" onChange={setReservationId} required />
        </FormGroup>
        <Button block color="primary" disabled={!isSubmitAllowed} id="submit" onClick={onSubmit}>
          {ts('find_reservation')}
        </Button>
      </ModalBody>
    </Modal>
  );
};
